/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import AuTaxType from '@linc-technologies/linced-common/enums/au-tax-type';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import config from 'linced-app/config/environment';
import TaxType from 'linced-app/enums/xero-tax-types';

export default class LedgersController extends Controller {
  @service
  router;

  // Services
  @service
  features;

  @service
  fetch;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @alias('session.school.name')
  schoolName;

  @optionAlias('finance:xero:enableXeroIntegration')
  xeroIntegrationEnabled;

  @optionAlias('finance:xero:piaLedgerCode')
  piaLedgerCode;

  @optionAlias('finance:xero:heroClearingAccountCode')
  heroClearingAccountCode;

  @tracked
  ledgerId = null;

  @tracked
  ledgerCode = '';

  @action
  setLedgerCode(value) {
    this.ledgerCode = value;
  }

  @tracked
  ledgerName = '';

  @action
  setLedgerName(value) {
    this.ledgerName = value;
  }

  @tracked
  ledgerTaxType = '';

  @tracked
  savingBulkLedgers = false;

  @tracked
  ledgerDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteLedgerDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  mdiSyncInProgress = false;

  @tracked
  errorMessage = '';

  @tracked
  selectedLedgersToAdd = [];

  @tracked
  xeroLedgerDialogOpen = false;

  @tracked
  showHideLedgerDialog = false;

  @tracked
  selectedLedgersToHide = [];

  @tracked
  hidingLedgers = false;

  allSelectedForHide = false;

  AuTaxType = AuTaxType;

  TaxType = TaxType;

  @tracked
  ledgerItemCount;

  @tracked
  ledgerLoaded;

  @tracked
  lineItemCount;

  @computed('model.xeroLedgers.[]', 'piaLedgerCode')
  get xeroLedgers() {
    return this.model.xeroLedgers.filter((ledger) => {
      return !(
        ledger.code === this.piaLedgerCode ||
        ledger.code === this.heroClearingAccountCode
      );
    });
  }

  // toggleDialog opens/closes the ledger dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('ledgerDialogOpen');
    if (!this.ledgerDialogOpen) {
      this.resetForm();
    }
  }

  @action
  toggleSelectAll() {
    this.toggleProperty('allSelectedForHide');
    if (this.allSelectedForHide) {
      this.selectedLedgersToHide = [
        ...this.selectedLedgersToHide,
        ...this.model.heroLedgers,
      ];
    } else {
      this.selectedLedgersToHide = [];
    }
  }

  @action
  toggleLedgerDialog() {
    this.toggleDialog();
  }

  @action
  toggleXeroLedgerDialog() {
    this.router.refresh('admin.finance.ledgers');
    this.selectedLedgersToAdd = [];
    this.toggleProperty('xeroLedgerDialogOpen');
  }

  @action
  toggleShowHideLedgerDialog() {
    this.selectedLedgersToHide = this.model.heroLedgers.filter(
      (item) => item.isHidden,
    );
    this.toggleProperty('showHideLedgerDialog');
  }

  @action
  editLedger(id) {
    this.store.findRecord('finance/ledger', id).then((ledger) => {
      this.ledgerCode = ledger.get('code');
      this.ledgerName = ledger.get('name');
    });
    this.ledgerId = id;
    this.toggleDialog();
  }

  @action
  addToLedgerList(ledger) {
    if (this.selectedLedgersToAdd.includes(ledger)) {
      this.selectedLedgersToAdd = this.selectedLedgersToAdd.filter(
        (l) => l !== ledger,
      );
    } else {
      this.selectedLedgersToAdd = [...this.selectedLedgersToAdd, ledger];
    }
  }

  @action
  addToHideLedgerList(ledger) {
    if (this.selectedLedgersToHide.includes(ledger)) {
      this.selectedLedgersToHide = this.selectedLedgersToHide.filter(
        (l) => l !== ledger,
      );
    } else {
      this.selectedLedgersToHide = [...this.selectedLedgersToHide, ledger];
    }
  }

  @action
  hideSelectedLedgers() {
    this.hidingLedgers = true;
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/ledgers/:bulkUpdateLedgerVisibility`;
    let selectedLedgerIds = this.selectedLedgersToHide.map((item) => item.id);
    let data = {
      schoolId: this.session.activeSchool,
      ledgerIds: selectedLedgerIds,
    };
    return this.fetch
      .post(url, { data })
      .then(() => {
        this.router.refresh('admin.finance.ledgers');
        this.toaster.success(`Ledgers have been hidden successfully!`);
      })
      .catch(() => {
        this.toaster.error(`Error in hiding ledgers`);
      })
      .finally(() => {
        this.hidingLedgers = false;
        this.showHideLedgerDialog = false;
      });
  }

  @action
  saveSelectedLedgers() {
    if (isEmpty(this.selectedLedgersToAdd)) {
      this.toaster.error('Please select ledgers to proceed.');
      this.savingBulkLedgers = false;
      return;
    }
    this.savingBulkLedgers = true;
    let promises = [];
    this.selectedLedgersToAdd.forEach((ledger) => {
      let promise;
      let record = this.store.createRecord('finance/ledger', {
        code: ledger.code,
        name: ledger.name,
        taxType: ledger.taxType,
      });

      promise = record.save().then(() => {
        return Promise.resolve();
      });
      promises.push(promise);
    });

    Promise.all(promises)
      .then(
        () => {
          let label = promises.length === 1 ? 'ledger' : 'ledgers';
          this.toaster.success(
            `${promises.length} ${label} created successfully!`,
          );
        },
        () => {
          this.toaster.error('Error saving ledgers. Please try again.');
        },
      )
      .finally(() => {
        this.savingBulkLedgers = false;
        this.xeroLedgerDialogOpen = false;
        this.router.refresh('admin.finance.ledgers');
      });
  }

  @action
  saveLedger() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.ledgerId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('finance/ledger', {
          code: this.ledgerCode,
          name: this.ledgerName,
          taxType: this.ledgerTaxType,
        });
        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.ledgerId;
        this.store.findRecord('finance/ledger', id).then((ledger) => {
          ledger.set('code', this.ledgerCode);
          ledger.set('name', this.ledgerName);
          ledger.set('taxType', this.ledgerTaxType);

          resolve(ledger);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          if (action === 'create') {
            // Send an event to trigger the route to refresh the model
            this.router.refresh('admin.finance.ledgers');
          }

          this.ledgerDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deleteLedger(id) {
    this.errorMessage = '';
    this.ledgerLoaded = false;
    hash({
      ledger: this.store.findRecord('finance/ledger', id),
      ledgerItems: this.store.query('finance/ledger-item', { ledgerId: id }),
      lineItems: this.store.query('finance/line-item', { ledgerId: id }),
    }).then((data) => {
      this.ledgerCode = data.ledger.get('code');
      this.ledgerName = data.ledger.get('name');
      this.ledgerTaxType = data.ledger.get('taxType');
      this.ledgerItemCount = data.ledgerItems.length;
      this.lineItemCount = data.lineItems.length;
      this.ledgerLoaded = true;
    });
    this.ledgerId = id;
    this.deleteLedgerDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let record = this.store.peekRecord('finance/ledger', this.ledgerId);

        // Delete record (locally + pushed to server).
        return record
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            record.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteLedgerDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  @action
  syncMDIs() {
    this.mdiSyncInProgress = true;
    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/nsw/sap/mdis/:sync`;
    let data = {
      schoolId: this.session.activeSchool,
    };
    return this.fetch
      .post(url, { data })
      .then(() => {
        this.toaster.success(`Master Data Items synced successfully.`);
        this.model.heroLedgers.update();
      })
      .catch(() => {
        this.toaster.error(`Error syncing Master Data Items!`);
      })
      .finally(() => {
        this.mdiSyncInProgress = false;
      });
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else if (!isEmpty(err.errors[0])) {
      let error = err.errors[0];
      if (!isEmpty(error.detail)) {
        this.errorMessage = error.detail;
      }
    } else {
      this.errorMessage = err.message;
    }
  }

  resetForm() {
    this.ledgerId = null;
    this.ledgerCode = '';
    this.ledgerName = '';
  }
}
